import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import { classNames, debounceHandler } from "../../utils/helpers";
import { View } from "../view";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect";
import { CarouselControl } from './CarouselControl';
import s from './Carousel.module.css';
export const Carousel = ({ children, controlRef, className, attributes, backButtonTitle = 'Back', forwardButtonTitle = 'Forward', hideControls = false, }) => {
    const [mounted, setMounted] = React.useState(false);
    const [scrollPosition, setScrollPosition] = React.useState(0);
    const measureElRef = React.useRef(null);
    const scrollElRef = React.useRef(null);
    const rootClassNames = classNames(s.root, className);
    const handleScroll = debounceHandler((event) => {
        const el = event.target;
        setScrollPosition(el.scrollLeft);
    }, 16);
    const getItemsGap = () => {
        const measureEl = measureElRef.current;
        if (!measureEl)
            return 0;
        const style = getComputedStyle(measureEl);
        const marginRight = style.marginRight;
        return parseInt(marginRight.replace('px', ''));
    };
    const navigateRight = () => {
        const scrollEl = scrollElRef.current;
        if (!scrollEl)
            return;
        scrollEl.scrollBy({
            left: scrollEl.clientWidth + getItemsGap(),
            top: 0,
            behavior: 'smooth',
        });
    };
    const navigateLeft = () => {
        const scrollEl = scrollElRef.current;
        if (!scrollEl)
            return;
        scrollEl.scrollBy({
            left: -scrollEl.clientWidth - getItemsGap(),
            top: 0,
            behavior: 'smooth',
        });
    };
    const navigateBack = navigateLeft;
    const navigateForward = navigateRight;
    React.useImperativeHandle(controlRef, () => ({
        navigateBack,
        navigateForward,
    }));
    /**
     * Changing flag here since scroll ref changing won't rerender the controls and show them after SSR
     */
    useIsomorphicLayoutEffect(() => {
        setMounted(true);
    }, []);
    return (_jsxs("section", Object.assign({ "data-testid": "st-carousel" }, attributes, { className: rootClassNames, children: [_jsx(View, { as: "ul", direction: "row", wrap: false, gap: 4, className: s.scroll, attributes: { ref: scrollElRef, onScroll: handleScroll, tabIndex: 0 }, children: React.Children.map(children, (child, index) => {
                    return (_jsx(View.Item, { className: s.item, as: "li", attributes: { ref: index === 0 ? measureElRef : undefined }, children: child }));
                }) }), !hideControls && (_jsxs(_Fragment, { children: [_jsx(CarouselControl, { type: 'back', buttonTitle: backButtonTitle, scrollElRef: scrollElRef, scrollPosition: scrollPosition, onClick: navigateBack, mounted: mounted }), _jsx(CarouselControl, { type: 'forward', buttonTitle: forwardButtonTitle, scrollElRef: scrollElRef, scrollPosition: scrollPosition, onClick: navigateForward, mounted: mounted })] }))] })));
};
